/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { CertsApiService, SSLCiphersApiService } from '../../../http';
import { CertModel, L7SSLCert, SSLCipherList } from '../../../models';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DropdownModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService, UtilsService } from '../../../../../../core/services';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ssl-offload-add-cert',
    templateUrl: './addCert.component.html',
    styleUrls: ['../../../../../../../assets/shared/scss/embedded.scss']
})

export class AddCertComponent extends BaseComponent {
    /**
     * Determines the way we style the component in and out of the wizard.
     */
    @Input() public componentEmbedded: boolean = false;

    /**
     * Used when adding or editing to store the ssl data.
     */
    @Input() set SSLCerts(sslCerts: L7SSLCert[]) {
        this._SSLCerts = sslCerts || [];
    }

    /**
     * Store the used certificates.
     */
    @Input() public certsInUse: string[] = [];

    /**
     * Get minimum cipher security version from Frontend Services.
     */
    @Input() public minCipherSecVersion: string;

    /**
     * Get maximum cipher security version from Frontend Services.
     */
    @Input() public maxCipherSecVersion: string;

    /**
     * Sends the ssl array out of the component.
     */
    @Output() public submittedCerts: EventEmitter<any[]> = new EventEmitter<any[]>();

    /**
     * Emit event to close cert page.
     */
    @Output() public closeCertPage: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * SSL certificates form.
     */
    public sslCertificatesForm: FormGroup;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown = {
        certificates: [],
        ciphers: []
    };

    /**
     * An array containing certificate models of certs currently available on the LB
     * @var SSLCertificates
     */
    public SSLCertificates: CertModel[] = [];

    /**
     * @var userCipherLists
     * The array containing the cipher lists available to the userManagement
     */
    public userCipherLists: SSLCipherList[] = [];

    /**
     * List of valid cipher lists.
     * @private
     */
    private validCipherLists: any[] = [];

    /**
     * List of all ciphers
     * @private
     */
    private allCiphers: any[] = [];

    /**
     * Store the initial data for form comparison.
     * @private
     */
    private initialData: any = {};

    /**
     * Store selected certificates for getter/setter
     * @private
     */
    private _SSLCerts: L7SSLCert[];

    constructor(
        private formBuilder: FormBuilder,
        private certsApiService: CertsApiService,
        private ciphersApiService: SSLCiphersApiService,
        private logService: LogService,
        private utilsService: UtilsService
    ) {
        super();

        this.setupForm();
        this.setupFormSubscribers();
        this.initLoading();
    }

    /**
     * Certificates getter.
     * @constructor
     */
    get SSLCerts() {
        return this._SSLCerts;
    }

    /**
     * Update cipher drop-down.
     */
    public updateCipherListDropdown(): any {
        const securityVersions: string[] = ['TLSv1.0', 'TLSv1.1', 'TLSv1.2', 'TLSv1.3'];
        const minSecurityVersion: string = this.minCipherSecVersion;
        const maxSecurityVersion: string = this.maxCipherSecVersion;
        const versionRange = this.getMinMaxIndex(minSecurityVersion, maxSecurityVersion);

        const allAvailableCiphers: any[] = this.allCiphers;
        const validCiphers: any[] = [];
        let versionIndex: number;
        for (const cipher of allAvailableCiphers) {
            for (const version of cipher.tlsVersion) {
                versionIndex = securityVersions.indexOf(version);
                if (versionIndex >= versionRange.min && versionIndex <= versionRange.max) {
                    validCiphers.push(cipher);
                }
            }
        }

        this.dropdown.ciphers = this.validateCipherLists(validCiphers);

        return this.dropdown.ciphers;
    }

    /**
     * Return to ssl offload page.
     */
    public goBack(): void {
        this.closeCertPage.emit(false);
    }

    /**
     * Submit new certificate.
     */
    public submitCert(): void {
        const sslCertificateId = this.sslCertificatesForm.get('sslCertificates').value;
        const cipherList = this.sslCertificatesForm.get('ciphers').value;
        this.addACertificateToVIP(sslCertificateId, cipherList);
    }

    /**
     * Add new certificate to certs
     * @private
     */
    private addACertificateToVIP(sslCertificateId: string, cipherList: any): void {
        const sslCerts: L7SSLCert[] = this.SSLCerts;
        sslCerts.push({
            certDenyHostnames: [],
            certMatchHostnames: [],
            certificateId: sslCertificateId,
            cipherListId: cipherList,
        });

        this.SSLCerts = sslCerts;
        this.certsInUse.push(sslCertificateId);
        this.submittedCerts.emit(this.SSLCerts);
    }

    /**
     * Validate cipher list
     * @param validCiphers
     * @private
     */
    private validateCipherLists(validCiphers: any[]): any[] {
        const validLists = [];
        for (const cipher of validCiphers) {
            for (const list of this.userCipherLists) {
                for (const item of list.ciphers) {
                    if (cipher.name === item) {
                        validLists.push(list.name);
                    }
                }
            }
        }

        this.validCipherLists = Array.from(new Set(validLists));

        return this.dropdown.ciphers.filter(item => this.validCipherLists.includes(item.value));
    }

    /**
     * Get the certificates and cipher lists from API and set class variables
     * Once set, set up the dropdowns
     * @private
     */
    private initLoading(): void {
        forkJoin([
            this.certsApiService.getCerts(),
            this.ciphersApiService.getAllCiphers(),
            this.ciphersApiService.getAllCipherLists(),
        ])
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups): void => {
                    this.SSLCertificates = dataGroups[0];
                    this.allCiphers = dataGroups[1];
                    this.userCipherLists = dataGroups[2];

                    this.setUpDropdownContent();
                },
                error: (error): void => {
                    this.logService.error('AddCertComponent: initLoading - failed to load data ', error);
                }
            });
    }

    /**
     * Setup dropdown arrays object.
     * @private
     */
    private setUpDropdownContent(): void {
        this.dropdown.certificates = this.getDropdownCertificates();
        this.dropdown.ciphers = this.getDropDownCiphers();
    }

    /**
     * Get the dropdown values for the certificates
     * @private
     */
    private getDropdownCertificates(): DropdownModel[] {
        const dropCert: DropdownModel[] = [];
        for (const cert of this.SSLCertificates) {
            if ((!this.certsInUse.includes(cert.id)) && cert['type'] !== 'CSR') {
                dropCert.push(<DropdownModel>{ key: cert.id, value: cert.name });
            }
        }

        return dropCert;
    }

    /**
     * Get the dropdown values for the cipher options
     * @private
     */
    private getDropDownCiphers(): DropdownModel[] {
        const dropList = [];

        if (this.userCipherLists) {
            for (const list of this.userCipherLists) {
                dropList.push(<DropdownModel>{ key: list.id, value: list.name });
            }
        }

        return dropList;
    }

    /**
     * Get min/max index
     * @param min
     * @param max
     * @private
     */
    private getMinMaxIndex(min: string, max: string): any {
        const tlsVersions: string[] = ['TLSv1.0', 'TLSv1.1', 'TLSv1.2', 'TLSv1.3'];
        let minIndex;
        let maxIndex;

        for (const version of tlsVersions) {
            if (version === min) {
                minIndex = tlsVersions.indexOf(version);
            }

            if (version === max) {
                maxIndex = tlsVersions.indexOf(version);
            }
        }

        return { min: minIndex, max: maxIndex };
    }

    /**
     * Setup the form.
     * @private
     */
    private setupForm(): void {
        this.sslCertificatesForm = this.formBuilder.group({
            sslCertificates: [null, Validators.required],
            ciphers: [null, Validators.required]
        });

        this.initialData = this.sslCertificatesForm.value;
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        this.sslCertificatesForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (form) => {
                    this.utilsService.debounce('form', () => {
                        if (this.utilsService.hasFormDataChanged(this.initialData, form)) {
                            this.sslCertificatesForm.markAsDirty();
                        } else {
                            this.sslCertificatesForm.markAsPristine();
                        }
                    }, 250);
                },
                error: (error) => {
                    this.logService.error('AddCertComponent: setupFormSubscribers - error ', error);
                }
            });
    }
}
